import { useId } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import SectionInput from "@/components/AIComponents/ExportablesModal/templates/components/SectionInput";
import { KWL_INPUT_MAX_LENGTH } from "./KnowWonderLearnChart.constants";
import * as styles from "./KnowWonderLearnChart.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";

interface KnowWonderLearnData {
  article_title?: string;
  topic?: string;
  articleShortTitle?: string | undefined;
  directions?: string;
  know?: string;
  wonder?: string;
  learned?: string;
  sectionOneTitle?: string;
  sectionTwoTitle?: string;
  sectionThreeTitle?: string;
}

// Type guard to check if formData is KnowWonderLearnData
const isKnowWonderLearnData = (data: any): data is KnowWonderLearnData => {
  return (
    data &&
    typeof data === "object" &&
    ("topic" in data ||
      "articleShortTitle" in data ||
      "know" in data ||
      "wonder" in data ||
      "learned" in data ||
      "sectionOneTitle" in data ||
      "sectionTwoTitle" in data ||
      "sectionThreeTitle" in data)
  );
};

const KnowWonderLearnChart = ({
  formData,
  onChange,
  title = "KWL Chart",
  isStudent = false,
}: TemplateProps<KnowWonderLearnData>) => {
  const knowId = useId();
  const wonderId = useId();
  const learnedId = useId();

  // Ensure formData is treated as KnowWonderLearnData
  const kwlData = isKnowWonderLearnData(formData)
    ? formData
    : ({} as KnowWonderLearnData);

  const directionsText = `Before reading, complete the chart by writing down what you KNOW (K) and what you WONDER (W) about ${kwlData?.topic || "this topic"}. After you read the article(s), write down what you have LEARNED (L).`;

  const { directions = directionsText } = kwlData;

  const handleChange = (field: keyof KnowWonderLearnData, value: string) => {
    if (onChange) {
      const currentDirections = formData?.directions || (directionsText ?? "");
      onChange({
        ...kwlData,
        [field]: value,
        directions: field === "directions" ? value : currentDirections,
      });
    }
  };

  return (
    <div css={styles.wrapper}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        directions={
          <OrganizerDirections
            name="KWL"
            value={directions}
            handleChange={(e) => handleChange("directions", e.target.value)}
            isStudent={isStudent}
          />
        }
        articleTitle={kwlData?.article_title || kwlData?.articleShortTitle}
      />

      <div css={styles.kwlSection}>
        <div css={styles.kwlInnerSection}>
          <div css={styles.kwlHeader}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Know"
              value={kwlData?.sectionOneTitle || "Know"}
              onChange={(e) => handleChange("sectionOneTitle", e.target.value)}
              styleOverrides={styles.kwlSectionInput}
            />
          </div>
          <textarea
            id={knowId}
            data-qa-selector="exportables-kwl-know-input"
            value={kwlData?.know}
            onChange={(e) => handleChange("know", e.target.value)}
            aria-label="KWL Know Section"
            css={styles.kwlTextArea}
            maxLength={KWL_INPUT_MAX_LENGTH}
          />
        </div>
        <div css={styles.kwlInnerSection}>
          <div css={styles.kwlHeader}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Wonder"
              value={kwlData?.sectionTwoTitle || "Wonder"}
              onChange={(e) => handleChange("sectionTwoTitle", e.target.value)}
              styleOverrides={styles.kwlSectionInput}
            />
          </div>
          <textarea
            id={wonderId}
            data-qa-selector="exportables-kwl-wonder-input"
            value={kwlData?.wonder}
            onChange={(e) => handleChange("wonder", e.target.value)}
            aria-label="KWL Wonder Section"
            css={styles.kwlTextArea}
            maxLength={KWL_INPUT_MAX_LENGTH}
          />
        </div>
        <div css={{ ...styles.kwlInnerSection, borderRight: "none" }}>
          <div css={styles.kwlHeader}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Learned"
              value={kwlData?.sectionThreeTitle || "Learned"}
              onChange={(e) =>
                handleChange("sectionThreeTitle", e.target.value)
              }
              styleOverrides={styles.kwlSectionInput}
            />
          </div>
          <textarea
            id={learnedId}
            data-qa-selector="exportables-kwl-learned-input"
            value={kwlData?.learned}
            onChange={(e) => handleChange("learned", e.target.value)}
            aria-label="KWL Learned Section"
            css={styles.kwlTextArea}
            maxLength={KWL_INPUT_MAX_LENGTH}
          />
        </div>
      </div>
    </div>
  );
};

export default KnowWonderLearnChart;
